import React from 'react';

const SIZE = 80;
const CENTER = SIZE / 2;
const CIRCLE_R = 24;

/**
 * Resolves various value formats into a boolean "active" flag.
 */
function isActive(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === 'on' || v === '1' || v === 'true' || v === 'activ\u00e9';
  }
  return false;
}

/**
 * State widget for actuators and binary devices.
 * Displays a green circle + checkmark when ON, red circle + X when OFF.
 *
 * @param {{ value: boolean|string|number, label: string }} props
 */
export default function StateWidget({ value, label = '' }) {
  const active = isActive(value);
  const bgColor = active ? '#22C55E' : '#EF4444';
  const statusText = active ? 'ON' : 'OFF';

  return (
    <svg
      width={SIZE}
      height={SIZE}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      className="block"
      role="img"
      aria-label={`${label ? label + ' ' : ''}${statusText}`}
    >
      {/* Background circle */}
      <circle
        cx={CENTER}
        cy={CENTER - 6}
        r={CIRCLE_R}
        fill={bgColor}
        opacity={0.15}
        style={{ transition: 'fill 0.3s ease' }}
      />

      {/* Foreground circle border */}
      <circle
        cx={CENTER}
        cy={CENTER - 6}
        r={CIRCLE_R}
        fill="none"
        stroke={bgColor}
        strokeWidth={2.5}
        style={{ transition: 'stroke 0.3s ease' }}
      />

      {/* Icon: checkmark or X */}
      {active ? (
        <polyline
          points={`${CENTER - 8},${CENTER - 6} ${CENTER - 2},${CENTER} ${CENTER + 9},${CENTER - 13}`}
          fill="none"
          stroke={bgColor}
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ transition: 'stroke 0.3s ease' }}
        />
      ) : (
        <g style={{ transition: 'stroke 0.3s ease' }}>
          <line
            x1={CENTER - 7}
            y1={CENTER - 13}
            x2={CENTER + 7}
            y2={CENTER + 1}
            stroke={bgColor}
            strokeWidth={3}
            strokeLinecap="round"
          />
          <line
            x1={CENTER + 7}
            y1={CENTER - 13}
            x2={CENTER - 7}
            y2={CENTER + 1}
            stroke={bgColor}
            strokeWidth={3}
            strokeLinecap="round"
          />
        </g>
      )}

      {/* Status label */}
      <text
        x={CENTER}
        y={SIZE - 6}
        textAnchor="middle"
        dominantBaseline="auto"
        className="font-medium"
        style={{ fontSize: 11, fill: bgColor, transition: 'fill 0.3s ease' }}
      >
        {label || statusText}
      </text>
    </svg>
  );
}
